import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SectionLabel } from "./primitives";

/**
 * [03] Drone compatibility. Wide strip of silhouettes — no brand
 * logos (trademark headache) — with each family's sensor characterised
 * by GSD/payload notes. Closes with a "bring your own GeoTIFF"
 * catch-all so flight-software people don't feel locked out.
 */
export function Drones() {
  const { t } = useTranslation();
  return (
    <section id="drones" className="relative border-b border-[color:var(--color-app-edge)]">
      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10 py-24 md:py-28">
        <SectionLabel
          index={t("drones.section.index")}
          title={t("drones.section.title")}
          kicker={t("drones.section.kicker")}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          <h2 className="text-display text-5xl md:text-6xl">
            {t("drones.headline.before")}{" "}
            <span className="text-accent text-accent-violet">
              {t("drones.headline.accent")}
            </span>{" "}
            {t("drones.headline.after")}
          </h2>
          <div className="md:pl-8 md:border-l md:border-[color:var(--color-app-edge)] text-[color:var(--color-app-text)]/75 leading-relaxed flex items-center">
            <p>{t("drones.intro")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--color-app-edge)] border border-[color:var(--color-app-edge)]">
          <DroneCell cellKey="consumer" silhouette={<DroneQuadFolding />} />
          <DroneCell cellKey="mapping"  silhouette={<DroneQuadCompact />} />
          <DroneCell cellKey="pro"      silhouette={<DroneQuadX />} />
          <DroneCell cellKey="survey"   silhouette={<DroneHex />} />
        </div>

        <div className="mt-px grid grid-cols-1 lg:grid-cols-3 gap-px bg-[color:var(--color-app-edge)] border-x border-b border-[color:var(--color-app-edge)]">
          <BlockNote noteKey="geotiff" />
          <BlockNote noteKey="clipsets" />
          <BlockNote noteKey="thermal" />
        </div>
      </div>
    </section>
  );
}

function DroneCell({
  cellKey,
  silhouette,
}: {
  cellKey: "consumer" | "mapping" | "pro" | "survey";
  silhouette: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-[color:var(--color-app-bg)] px-6 py-8 flex flex-col gap-5">
      <div className="h-20 flex items-center justify-center text-[color:var(--color-app-text)]/80">
        {silhouette}
      </div>
      <div>
        <div className="font-display text-xl leading-tight mb-1">
          {t(`drones.cells.${cellKey}.family`)}
        </div>
        <div className="coord-mono opacity-70">{t(`drones.cells.${cellKey}.payload`)}</div>
      </div>
      <div className="border-t border-[color:var(--color-app-edge)] pt-4 flex items-baseline justify-between">
        <div className="label-mono opacity-60">{t("drones.gsdLabel")}</div>
        <div className="font-mono text-sm num">{t(`drones.cells.${cellKey}.gsd`)}</div>
      </div>
    </div>
  );
}

function BlockNote({
  noteKey,
}: {
  noteKey: "geotiff" | "clipsets" | "thermal";
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-[color:var(--color-app-bg)] px-6 py-5">
      <div className="label-mono opacity-70 mb-1.5">{t(`drones.notes.${noteKey}.label`)}</div>
      <div className="text-sm text-[color:var(--color-app-text)]/75">{t(`drones.notes.${noteKey}.body`)}</div>
    </div>
  );
}

/* ── Inline drone silhouettes ────────────────────────────────────── */

function DroneQuadFolding() {
  return (
    <svg viewBox="0 0 160 80" className="h-full" fill="none" stroke="currentColor" strokeWidth={1.2}>
      <path d="M40 22 L26 8" strokeLinecap="round" />
      <path d="M120 22 L134 8" strokeLinecap="round" />
      <path d="M40 58 L26 72" strokeLinecap="round" />
      <path d="M120 58 L134 72" strokeLinecap="round" />
      <circle cx={26} cy={8} r={9} />
      <circle cx={134} cy={8} r={9} />
      <circle cx={26} cy={72} r={9} />
      <circle cx={134} cy={72} r={9} />
      {/* Body */}
      <rect x={40} y={22} width={80} height={36} rx={4} />
      <rect x={56} y={32} width={48} height={16} rx={2} />
      {/* Gimbal */}
      <rect x={72} y={60} width={16} height={10} rx={2} />
      <circle cx={80} cy={66} r={3} fill="currentColor" />
    </svg>
  );
}

function DroneQuadCompact() {
  return (
    <svg viewBox="0 0 160 80" className="h-full" fill="none" stroke="currentColor" strokeWidth={1.2}>
      <rect x={56} y={28} width={48} height={24} rx={3} />
      <ellipse cx={80} cy={40} rx={18} ry={9} />
      {/* Arms */}
      <line x1={56} y1={28} x2={30} y2={12} strokeLinecap="round" />
      <line x1={104} y1={28} x2={130} y2={12} strokeLinecap="round" />
      <line x1={56} y1={52} x2={30} y2={68} strokeLinecap="round" />
      <line x1={104} y1={52} x2={130} y2={68} strokeLinecap="round" />
      <ellipse cx={30} cy={12} rx={12} ry={3} />
      <ellipse cx={130} cy={12} rx={12} ry={3} />
      <ellipse cx={30} cy={68} rx={12} ry={3} />
      <ellipse cx={130} cy={68} rx={12} ry={3} />
      {/* Camera */}
      <circle cx={80} cy={60} r={4} fill="currentColor" />
    </svg>
  );
}

function DroneQuadX() {
  return (
    <svg viewBox="0 0 160 80" className="h-full" fill="none" stroke="currentColor" strokeWidth={1.2}>
      <rect x={56} y={28} width={48} height={24} rx={4} />
      {/* X arms */}
      <line x1={56} y1={28} x2={24} y2={4}  strokeLinecap="round" />
      <line x1={104} y1={28} x2={136} y2={4}  strokeLinecap="round" />
      <line x1={56} y1={52} x2={24} y2={76} strokeLinecap="round" />
      <line x1={104} y1={52} x2={136} y2={76} strokeLinecap="round" />
      <circle cx={24}  cy={4}  r={8} />
      <circle cx={136} cy={4}  r={8} />
      <circle cx={24}  cy={76} r={8} />
      <circle cx={136} cy={76} r={8} />
      <circle cx={80}  cy={40} r={5} fill="currentColor" />
      <rect x={74} y={50} width={12} height={14} rx={2} />
    </svg>
  );
}

function DroneHex() {
  return (
    <svg viewBox="0 0 160 80" className="h-full" fill="none" stroke="currentColor" strokeWidth={1.2}>
      <ellipse cx={80} cy={40} rx={22} ry={12} />
      {/* Six arms */}
      {[
        { x: 22,  y: 8 },
        { x: 138, y: 8 },
        { x: 12,  y: 40 },
        { x: 148, y: 40 },
        { x: 22,  y: 72 },
        { x: 138, y: 72 },
      ].map((p, i) => (
        <g key={i}>
          <line x1={80} y1={40} x2={p.x} y2={p.y} strokeLinecap="round" />
          <circle cx={p.x} cy={p.y} r={7} />
        </g>
      ))}
      <circle cx={80} cy={40} r={4} fill="currentColor" />
      <rect x={72} y={48} width={16} height={12} rx={2} />
    </svg>
  );
}
